import {
  doc, getDoc, setDoc, getDocs, deleteDoc, collection, query, where,
  writeBatch, serverTimestamp, limit, documentId,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { getStudyTimeTotal } from './studyTimeService';
import { MASTERY_THRESHOLD, getCardStates } from './progressService';
import { computeMasteryScore, retentionToFactor } from './tamkeenEngine';
import { getJourneysByClass } from './journeyService';
import { getActiveClasses } from './classService';
import { todayStr } from '../utils/helpers';

interface CardState {
  id: string;
  c?: number;
  w?: number;
  s?: number;
  [key: string]: unknown;
}

interface RetentionEntry {
  classId: string;
  className: string;
  retention: number;
  studied: number;
  declining: number;
  accuracy: number;
}

export interface QuickStats {
  studyTimeSeconds: number;
  accuracy: number;
  lastSeen: Date | null;
  retention: number | null;
  retentionByClass: RetentionEntry[];
  mastered: number;
  attemptedCards: number;
  systemCards: number;
}

export interface StudentOverview extends QuickStats {
  totalStudySeconds: number;
  totalAttempts: number;
  streak: number;
  journeysStarted: number;
  journeysDone: number;
  studentClassList: { classId: string; className: string }[];
  _snapshotExists: boolean;
  _snapshotData?: unknown;
}

export interface Snapshot {
  date: string;
  accuracy: number;
  mastered: number;
  totalAttempts: number;
  totalCards: number;
  mastery: number | null;
  byClass: Record<string, unknown>;
  takenAt?: unknown;
}

async function getAllCardStates(uid: string): Promise<CardState[]> {
  const col = collection(db, 'progress', uid, 'cards');
  const snap = await getDocs(col);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CardState);
}

async function getAllJourneyProgress(uid: string): Promise<Record<string, unknown>[]> {
  const col = collection(db, 'progress', uid, 'journeys');
  const snap = await getDocs(col);
  return snap.docs.map((d) => d.data());
}

async function getStudentAvailableCards(uid: string, userClasses: string[] | null = null): Promise<number> {
  let classes = userClasses;
  if (classes === null) {
    const userSnap = await getDoc(doc(db, 'users', uid));
    classes = userSnap.exists() ? ((userSnap.data().classes || []) as string[]) : [];
  }
  if (classes.length === 0) return 0;

  const decksCol = collection(db, 'decks');
  const batchSize = 30;
  let total = 0;
  for (let i = 0; i < classes.length; i += batchSize) {
    const batch = classes.slice(i, i + batchSize);
    const q = query(decksCol, where('classId', 'in', batch));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => { total += (d.data().cardCount as number) || 0; });
  }
  return total;
}

export async function computeRetentionByClass(
  statesMap: Record<string, unknown>,
  studentClasses: string[],
  journeysCache: Map<string, unknown[]> | null = null,
  classesCache: { id: string; name: string; desiredRetention?: number }[] | null = null,
): Promise<RetentionEntry[]> {
  if (!studentClasses || studentClasses.length === 0) return [];
  const allClasses = classesCache || await getActiveClasses();
  const classMap: Record<string, string> = {};
  const classRetention: Record<string, number> = {};
  for (const c of allClasses) { classMap[c.id] = c.name; classRetention[c.id] = retentionToFactor((c as Record<string, unknown>).desiredRetention); }

  async function getJourneysCached(classId: string) {
    if (!journeysCache) return getJourneysByClass(classId);
    if (journeysCache.has(classId)) return journeysCache.get(classId)!;
    const result = await getJourneysByClass(classId);
    journeysCache.set(classId, result);
    return result;
  }

  const classJourneys = await Promise.all(
    studentClasses.map(cid => getJourneysCached(cid).then(jList => ({ classId: cid, journeys: jList as Record<string, unknown>[] })))
  );

  const result: RetentionEntry[] = [];
  for (const { classId, journeys: jList } of classJourneys) {
    const cardIdsForClass = new Set<string>();
    for (const j of jList) {
      if (j.trashedAt || j.active === false || j.standalone) continue;
      for (const g of ((j.groups || []) as Record<string, unknown>[])) {
        const cids = (g.cardIds || g.wordCardIds || g.flashcardIds || g.questionCardIds || []) as string[];
        cids.forEach(id => cardIdsForClass.add(id));
      }
    }
    if (cardIdsForClass.size === 0) continue;
    const classStates: Record<string, unknown> = {};
    let totalC = 0, totalW = 0;
    for (const cid of cardIdsForClass) {
      if (statesMap[cid]) {
        classStates[cid] = statesMap[cid];
        const state = statesMap[cid] as CardState;
        totalC += state.c || 0;
        totalW += state.w || 0;
      }
    }
    const m = computeMasteryScore(classStates, classRetention[classId] || 9);
    const totalAttempts = totalC + totalW;
    if (m.score >= 0) {
      result.push({
        classId,
        className: classMap[classId] || classId,
        retention: m.score,
        studied: m.studied,
        declining: m.declining,
        accuracy: totalAttempts > 0 ? Math.round((totalC / totalAttempts) * 100) : 0,
      });
    }
  }
  return result;
}

function computeStatsFromCards(cards: CardState[]) {
  let totalCorrect = 0;
  let totalWrong = 0;
  let mastered = 0;

  for (const c of cards) {
    totalCorrect += c.c || 0;
    totalWrong += c.w || 0;
    if ((c.s || 0) >= MASTERY_THRESHOLD) mastered++;
  }

  const totalAttempts = totalCorrect + totalWrong;
  const accuracy = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;

  return { accuracy, mastered, totalCards: cards.length, totalAttempts, totalCorrect, totalWrong };
}

export async function getStudentQuickStatsBatch(
  uids: string[],
  classesCache: { id: string; name: string }[] | null = null,
): Promise<{ results: Record<string, QuickStats>; slowUids: string[] }> {
  if (uids.length === 0) return { results: {}, slowUids: [] };

  const allMeta: Record<string, Record<string, unknown>> = {};
  const BATCH = 30;
  for (let i = 0; i < uids.length; i += BATCH) {
    const batch = uids.slice(i, i + BATCH);
    const q = query(collection(db, 'progress'), where(documentId(), 'in', batch));
    const snap = await getDocs(q);
    snap.docs.forEach(d => { allMeta[d.id] = d.data(); });
  }

  const classMap: Record<string, string> = {};
  for (const c of (classesCache || [])) classMap[c.id] = c.name;

  const results: Record<string, QuickStats> = {};
  const slowUids: string[] = [];

  for (const uid of uids) {
    const meta = allMeta[uid] || {};
    const cs = meta.cardStats as Record<string, unknown> | undefined;

    if (cs && cs.mastery !== undefined) {
      const totalAttempts = ((cs.totalC as number) || 0) + ((cs.totalW as number) || 0);
      const accuracy = totalAttempts > 0 ? Math.round(((cs.totalC as number) / totalAttempts) * 100) : 0;
      const lastSeen = (meta.lastSeen as { toDate?: () => Date })?.toDate?.() || null;

      const retentionByClass = ((cs.retentionByClass || []) as RetentionEntry[]).map(r => ({
        ...r,
        className: classMap[r.classId] || r.classId,
      }));

      results[uid] = {
        studyTimeSeconds: (meta.totalStudySeconds as number) || 0,
        accuracy,
        lastSeen,
        retention: cs.mastery as number,
        retentionByClass,
        mastered: 0,
        attemptedCards: (cs.studied as number) || 0,
        systemCards: 0,
      };
    } else {
      slowUids.push(uid);
    }
  }

  return { results, slowUids };
}

export async function getStudentQuickStats(
  uid: string,
  journeysCache: Map<string, unknown[]> | null = null,
  classesCache: { id: string; name: string; desiredRetention?: number }[] | null = null,
): Promise<QuickStats> {
  const metaSnap = await getDoc(doc(db, 'progress', uid));
  const meta = metaSnap.exists() ? metaSnap.data() : {} as Record<string, unknown>;
  const cs = meta.cardStats as Record<string, unknown> | undefined;

  if (cs && cs.mastery !== undefined) {
    const totalAttempts = ((cs.totalC as number) || 0) + ((cs.totalW as number) || 0);
    const accuracy = totalAttempts > 0 ? Math.round(((cs.totalC as number) / totalAttempts) * 100) : 0;
    const lastSeen = (meta.lastSeen as { toDate?: () => Date })?.toDate?.() || null;

    const allClasses = classesCache || [];
    const classMap: Record<string, string> = {};
    for (const c of allClasses) classMap[c.id] = c.name;

    const retentionByClass = ((cs.retentionByClass || []) as RetentionEntry[]).map(r => ({
      ...r,
      className: classMap[r.classId] || r.classId,
    }));

    return {
      studyTimeSeconds: (meta.totalStudySeconds as number) || 0,
      accuracy,
      lastSeen,
      retention: cs.mastery as number,
      retentionByClass,
      mastered: 0,
      attemptedCards: (cs.studied as number) || 0,
      systemCards: 0,
    };
  }

  const [studyTime, cards, userSnap] = await Promise.all([
    getStudyTimeTotal(uid),
    getAllCardStates(uid),
    getDoc(doc(db, 'users', uid)),
  ]);
  const lastSeen = (meta.lastSeen as { toDate?: () => Date })?.toDate?.() || null;

  const studentClasses = userSnap.exists() ? ((userSnap.data().classes || []) as string[]) : [];
  const systemCards = await getStudentAvailableCards(uid, studentClasses);

  const { accuracy, mastered, totalCards: attemptedCards } = computeStatsFromCards(cards);

  const statesMap: Record<string, unknown> = {};
  for (const c of cards) {
    if (c.id) statesMap[c.id] = c;
  }
  const mastery = computeMasteryScore(statesMap);

  let retentionByClass: RetentionEntry[] = [];
  try {
    retentionByClass = await computeRetentionByClass(statesMap, studentClasses, journeysCache, classesCache);
  } catch (e) { console.error('quick retention by class error:', e); }

  return {
    studyTimeSeconds: studyTime.totalSeconds,
    accuracy,
    lastSeen,
    retention: mastery.score >= 0 ? mastery.score : null,
    retentionByClass,
    mastered,
    attemptedCards,
    systemCards,
  };
}

export async function getStudentOverview(uid: string): Promise<StudentOverview> {
  const todaySnapshotRef = doc(db, 'progress', uid, 'snapshots', todayStr());

  const [metaSnap, userSnap, journeys, todaySnap] = await Promise.all([
    getDoc(doc(db, 'progress', uid)),
    getDoc(doc(db, 'users', uid)),
    getAllJourneyProgress(uid),
    getDoc(todaySnapshotRef),
  ]);

  const meta = metaSnap.exists() ? metaSnap.data() : {} as Record<string, unknown>;
  const cs = meta.cardStats as Record<string, unknown> | undefined;
  const studentClasses = userSnap.exists() ? ((userSnap.data().classes || []) as string[]) : [];

  const journeysStarted = journeys.length;
  const journeysDone = journeys.filter((j) => j.done).length;

  if (cs && cs.mastery !== undefined) {
    const totalAttempts = ((cs.totalC as number) || 0) + ((cs.totalW as number) || 0);
    const accuracy = totalAttempts > 0 ? Math.round(((cs.totalC as number) / totalAttempts) * 100) : 0;

    const [systemCards, allClasses] = await Promise.all([
      getStudentAvailableCards(uid, studentClasses),
      getActiveClasses(),
    ]);

    const classMap: Record<string, string> = {};
    for (const c of allClasses) classMap[c.id] = c.name;

    const retentionByClass = ((cs.retentionByClass || []) as RetentionEntry[]).map(r => ({
      ...r,
      className: classMap[r.classId] || r.classId,
    }));

    const studentClassList = studentClasses.map(cid => ({
      classId: cid, className: classMap[cid] || cid,
    }));

    if (!todaySnap.exists()) {
      const byClass: Record<string, unknown> = {};
      for (const r of ((cs.retentionByClass || []) as RetentionEntry[])) {
        byClass[r.classId] = {
          className: classMap[r.classId] || r.classId,
          accuracy: null,
          mastery: r.retention ?? null,
        };
      }
      setDoc(todaySnapshotRef, {
        accuracy,
        mastered: (cs.mastered as number) || 0,
        totalAttempts,
        totalCards: (cs.studied as number) || 0,
        mastery: cs.mastery,
        byClass,
        takenAt: serverTimestamp(),
      }).catch(() => {});
    }

    return {
      totalStudySeconds: (meta.totalStudySeconds as number) || 0,
      studyTimeSeconds: (meta.totalStudySeconds as number) || 0,
      accuracy,
      lastSeen: (meta.lastSeen as { toDate?: () => Date })?.toDate?.() || null,
      mastered: (cs.mastered as number) || 0,
      attemptedCards: (cs.studied as number) || 0,
      systemCards,
      totalAttempts,
      streak: (meta.streak as number) || 0,
      journeysStarted,
      journeysDone,
      retention: cs.mastery as number,
      retentionByClass,
      studentClassList,
      _snapshotExists: true,
    };
  }

  const cards = await getAllCardStates(uid);
  const cardStatsLive = computeStatsFromCards(cards);
  const allClasses = studentClasses.length > 0 ? await getActiveClasses() : [];
  const systemCards = await getStudentAvailableCards(uid, studentClasses);

  const statesMap: Record<string, unknown> = {};
  for (const c of cards) { if (c.id) statesMap[c.id] = c; }
  const overallMastery = computeMasteryScore(statesMap);

  let retentionByClass: RetentionEntry[] = [];
  let studentClassList: { classId: string; className: string }[] = [];
  try {
    if (studentClasses.length > 0) {
      const classMap: Record<string, string> = {};
      for (const c of allClasses) classMap[c.id] = c.name;
      studentClassList = studentClasses.map(cid => ({ classId: cid, className: classMap[cid] || cid }));
      retentionByClass = await computeRetentionByClass(statesMap, studentClasses, null, allClasses);
    }
  } catch (e) { console.error('retention by class error:', e); }

  return {
    totalStudySeconds: (meta.totalStudySeconds as number) || 0,
    studyTimeSeconds: (meta.totalStudySeconds as number) || 0,
    accuracy: cardStatsLive.accuracy,
    lastSeen: (meta.lastSeen as { toDate?: () => Date })?.toDate?.() || null,
    mastered: cardStatsLive.mastered,
    attemptedCards: cardStatsLive.totalCards,
    systemCards,
    totalAttempts: cardStatsLive.totalAttempts,
    streak: (meta.streak as number) || 0,
    journeysStarted,
    journeysDone,
    retention: overallMastery.score >= 0 ? overallMastery.score : null,
    retentionByClass,
    studentClassList,
    _snapshotExists: todaySnap.exists(),
    _snapshotData: todaySnap.exists() ? null : { cards, statesMap, studentClasses, allClasses },
  };
}

export async function ensureTodaySnapshot(uid: string, prefetched: Record<string, unknown> | null = null, skipExistenceCheck = false): Promise<Snapshot | null> {
  const key = todayStr();
  const ref = doc(db, 'progress', uid, 'snapshots', key);
  if (!skipExistenceCheck) {
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data() as Snapshot;
  }

  let cards: CardState[], statesMap: Record<string, unknown>, studentClasses: string[], cachedClasses;

  if (prefetched) {
    cards = prefetched.cards as CardState[];
    statesMap = prefetched.statesMap as Record<string, unknown>;
    studentClasses = prefetched.studentClasses as string[];
    cachedClasses = (prefetched.allClasses as { id: string; name: string; desiredRetention?: number }[]) || null;
  } else {
    const [fetchedCards, userSnap] = await Promise.all([
      getAllCardStates(uid),
      getDoc(doc(db, 'users', uid)),
    ]);
    cards = fetchedCards;
    statesMap = {};
    for (const c of cards) { if (c.id) statesMap[c.id] = c; }
    studentClasses = userSnap.exists() ? ((userSnap.data().classes || []) as string[]) : [];
    cachedClasses = null;
  }

  if (cards.length === 0) return null;

  const stats = computeStatsFromCards(cards);
  const masteryResult = computeMasteryScore(statesMap);

  const byClass: Record<string, unknown> = {};
  try {
    if (studentClasses.length > 0) {
      const retByClass = await computeRetentionByClass(statesMap, studentClasses, null, cachedClasses);
      for (const rc of retByClass) {
        byClass[rc.classId] = {
          className: rc.className,
          accuracy: rc.accuracy,
          mastery: rc.retention,
        };
      }
    }
  } catch (e) { console.error('snapshot byClass error:', e); }

  const snapshot = {
    accuracy: stats.accuracy,
    mastered: stats.mastered,
    totalAttempts: stats.totalAttempts,
    totalCards: stats.totalCards,
    mastery: masteryResult.score >= 0 ? masteryResult.score : null,
    byClass,
    takenAt: serverTimestamp(),
  };
  await setDoc(ref, snapshot);
  return snapshot as unknown as Snapshot;
}

export async function getSnapshots(uid: string): Promise<Snapshot[]> {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 1);
  cutoff.setMonth(cutoff.getMonth() - 1);
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;

  const col = collection(db, 'progress', uid, 'snapshots');
  const q = query(col, where(documentId(), '>=', cutoffStr), limit(400));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ date: d.id, ...d.data() }) as Snapshot)
    .sort((a, b) => a.date.localeCompare(b.date));
}

async function deleteSubcollection(parentPath: string, subName: string): Promise<number> {
  const col = collection(db, ...parentPath.split('/'), subName);
  const BATCH_LIMIT = 400;
  let deleted = 0;

  while (true) {
    const q = query(col, limit(BATCH_LIMIT));
    const snap = await getDocs(q);
    if (snap.empty) break;

    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    deleted += snap.docs.length;

    if (snap.docs.length < BATCH_LIMIT) break;
  }
  return deleted;
}

export async function resetStudentProgress(uid: string): Promise<boolean> {
  const basePath = `progress/${uid}`;

  const journeysCol = collection(db, basePath, 'journeys');
  const journeySnap = await getDocs(journeysCol);
  await Promise.all(
    journeySnap.docs.map(jDoc => deleteSubcollection(`${basePath}/journeys/${jDoc.id}`, 'steps'))
  );

  await deleteSubcollection(basePath, 'cards');
  await deleteSubcollection(basePath, 'decks');
  await deleteSubcollection(basePath, 'journeys');
  await deleteSubcollection(basePath, 'studyTime');
  await deleteSubcollection(basePath, 'snapshots');

  const metaRef = doc(db, 'progress', uid);
  const metaSnap = await getDoc(metaRef);
  if (metaSnap.exists()) {
    await deleteDoc(metaRef);
  }

  return true;
}
